/* eslint-disable react-refresh/only-export-components */
/**
 * Margin Settings Context
 * Manages margin settings for document export with server-backed sync
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MarginSettings, MarginSettingsContextValue, MarginPreset } from '@/types';
import {
  DEFAULT_MARGIN_SETTINGS,
  MARGIN_CONSTRAINTS,
  MARGIN_SETTINGS_STORAGE_KEY,
  MARGIN_PRESETS_STORAGE_KEY,
  MAX_MARGIN_PRESETS,
} from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { userPreferencesApi } from '@/services/api';
import { useDocumentSettingsState } from '@/hooks/useDocumentSettingsState';
import { debugEvent } from '@/lib/debugApi';
import { normalizePageDesign } from '@/types/pageDesign';

interface MarginSettingsProviderProps {
  children: React.ReactNode;
}

/**
 * Exported so the one-click format provider can supply its own values to the same editor
 * subtree (SPEC-ONECLICK-FORMAT-001 design.md §6). The hook below is unchanged.
 */
export const MarginSettingsContext = createContext<MarginSettingsContextValue | undefined>(
  undefined
);

/**
 * Validate margin settings from server or localStorage
 */
export const validateMarginSettings = (data: unknown): MarginSettings | null => {
  if (!data || typeof data !== 'object') return null;

  const settings = data as Record<string, unknown>;
  const requiredFields = ['top', 'bottom', 'left', 'right', 'colSpacing', 'mirrorMargins', 'columnCount'];

  for (const field of requiredFields) {
    if (!(field in settings)) return null;
  }

  // Validate types
  if (
    !Number.isFinite(settings.top) ||
    !Number.isFinite(settings.bottom) ||
    !Number.isFinite(settings.left) ||
    !Number.isFinite(settings.right) ||
    !Number.isFinite(settings.colSpacing) ||
    typeof settings.mirrorMargins !== 'boolean' ||
    (settings.columnCount !== 1 && settings.columnCount !== 2)
  ) {
    return null;
  }

  return data as MarginSettings;
};

/**
 * Clamp margin settings to valid ranges. Also the boundary that makes the two-column
 * separator a boolean: anything but false (a missing field included) is on
 * (SPEC-ONECLICK-FORMAT-001 REQ-OCF-014).
 */
export const clampMarginSettings = (settings: MarginSettings): MarginSettings => {
  const clampMargin = (value: number) =>
    Math.min(Math.max(value, MARGIN_CONSTRAINTS.MIN), MARGIN_CONSTRAINTS.MAX);
  const clampColSpacing = (value: number) =>
    Math.min(Math.max(value, MARGIN_CONSTRAINTS.COL_SPACING_MIN), MARGIN_CONSTRAINTS.COL_SPACING_MAX);

  return {
    top: clampMargin(settings.top),
    bottom: clampMargin(settings.bottom),
    left: clampMargin(settings.left),
    right: clampMargin(settings.right),
    colSpacing: clampColSpacing(settings.colSpacing),
    mirrorMargins: settings.mirrorMargins,
    columnCount: settings.columnCount,
    columnSeparator: settings.columnSeparator !== false,
    ...(settings.pageDesign ? { pageDesign: normalizePageDesign(settings.pageDesign) } : {}),
  };
};

/**
 * Load margin settings from localStorage
 */
const loadMarginSettings = (): MarginSettings => {
  try {
    const stored = localStorage.getItem(MARGIN_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const validated = validateMarginSettings(parsed?.settings ?? parsed);
      if (validated) {
        return clampMarginSettings(validated);
      }
    }
  } catch (err) {
    console.error('[MarginSettingsContext] Failed to load settings:', err);
  }
  return { ...DEFAULT_MARGIN_SETTINGS };
};

/**
 * Load updatedAt timestamp from localStorage
 */
const loadUpdatedAt = (): number => {
  try {
    const stored = localStorage.getItem(MARGIN_SETTINGS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.updatedAt ?? 0;
    }
  } catch {
    return 0;
  }
  return 0;
};

/**
 * Load margin presets from localStorage
 */
const loadMarginPresets = (): MarginPreset[] => {
  try {
    const stored = localStorage.getItem(MARGIN_PRESETS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed as MarginPreset[];
      }
    }
  } catch (err) {
    console.error('[MarginSettingsContext] Failed to load presets:', err);
  }
  return [];
};

/**
 * Generate a unique ID for presets
 */
const generatePresetId = (): string => {
  return `margin_preset_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

const resolvePresetSource = (currentPresets: MarginPreset[]): MarginPreset[] => {
  if (currentPresets.length > 0) {
    return currentPresets;
  }

  return loadMarginPresets().slice(0, MAX_MARGIN_PRESETS);
};

/**
 * Validate a margin value and clamp to constraints
 */
const validateMarginValue = (value: number, isColSpacing = false): number => {
  const min = isColSpacing ? MARGIN_CONSTRAINTS.COL_SPACING_MIN : MARGIN_CONSTRAINTS.MIN;
  const max = isColSpacing ? MARGIN_CONSTRAINTS.COL_SPACING_MAX : MARGIN_CONSTRAINTS.MAX;

  if (!Number.isFinite(value)) {
    return isColSpacing ? DEFAULT_MARGIN_SETTINGS.colSpacing : DEFAULT_MARGIN_SETTINGS.top;
  }

  return Math.min(Math.max(value, min), max);
};

const validatePresets = (data: unknown): MarginPreset[] => {
  if (!Array.isArray(data)) return [];
  const seen = new Set<string>();
  return data.flatMap((preset) => {
    if (!preset || typeof preset.id !== 'string' || !preset.id || seen.has(preset.id) ||
        typeof preset.name !== 'string' || !preset.name.trim()) return [];
    const settings = validateMarginSettings(preset.settings);
    if (!settings) return [];
    seen.add(preset.id);
    return [{ id: preset.id, name: preset.name.trim().slice(0, 100),
      settings: clampMarginSettings(settings), createdAt: Number.isFinite(preset.createdAt) ? preset.createdAt : 0 }];
  }).slice(0, MAX_MARGIN_PRESETS);
};

export function MarginSettingsProvider({ children }: MarginSettingsProviderProps) {
  const { user, isImpersonating } = useAuth();
  const canPersist = !!user && !isImpersonating;

  const [settings, setSettingsState, settingsRef] = useDocumentSettingsState<MarginSettings>({ ...DEFAULT_MARGIN_SETTINGS });
  const [updatedAt, setUpdatedAt] = useState<number>(0);
  const [presets, setPresetsState, presetsRef] = useDocumentSettingsState<MarginPreset[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isHydrating, setIsHydrating] = useState(!!user);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const scopeRef = useRef(0);
  const loadFailedRef = useRef(false);


  // The API owns the debounce so queued saves survive provider unmounts.
  const syncVersionRef = useRef(0);
  const isHydratingRef = useRef(false);

  // Queue immediately; userPreferencesApi merges rapid changes into one save.
  const syncToServer = useCallback(
    async (newSettings: MarginSettings, newPresets: MarginPreset[]) => {
      if (!canPersist || isHydratingRef.current) return;

      const scope = scopeRef.current;
      const syncVersion = ++syncVersionRef.current;
      setSyncError(null);
      setIsSyncing(true);
      try {
        debugEvent('MarginSettings.sync:commit');
        const response = await userPreferencesApi.update({
          marginSettings: newSettings,
          marginPresets: newPresets,
        });

        if (scope !== scopeRef.current || syncVersion !== syncVersionRef.current) return;
        if (response?.marginSettingsUpdatedAt) {
          setUpdatedAt(response.marginSettingsUpdatedAt);
        }
        debugEvent('MarginSettings.sync:success', {
          updatedAt: response?.marginSettingsUpdatedAt,
        });
      } catch (err) {
        console.error('[MarginSettingsContext] Server sync failed:', err);
        debugEvent('MarginSettings.sync:error', {
          error: err instanceof Error ? err.message : String(err),
        });
        if (scope === scopeRef.current && syncVersion === syncVersionRef.current) setSyncError('변경 사항을 저장하지 못했습니다. 다시 저장해 주세요.');
      } finally {
        if (scope === scopeRef.current && syncVersion === syncVersionRef.current) {
          setIsSyncing(false);
        }
      }
    },
    [canPersist]
  );

  // Hydrate from server on login
  useEffect(() => {
    const scope = ++scopeRef.current;
    let cancelled = false;
    setSettingsState({ ...DEFAULT_MARGIN_SETTINGS });
    setPresetsState([]);
    setUpdatedAt(0);
    setSyncError(null);
    setLoadFailed(false);
    loadFailedRef.current = false;
    setIsSyncing(false);
    isHydratingRef.current = !!user;
    setIsHydrating(!!user);
    if (!user) return;

    const hydrateFromServer = async () => {
      setIsHydrating(true);
      isHydratingRef.current = true;

      try {
        const legacySettings = loadMarginSettings();
        const legacyUpdatedAt = loadUpdatedAt();
        const legacyPresets = validatePresets(loadMarginPresets());
        debugEvent('MarginSettings.hydrate:start', { updatedAt });
        const serverPrefs = await userPreferencesApi.get();
        if (cancelled || scope !== scopeRef.current) return;

        if (serverPrefs) {
          const serverSettings = serverPrefs.marginSettings as MarginSettings | undefined;
          const serverPresets = validatePresets(serverPrefs.marginPresets);
          const serverUpdatedAt = (serverPrefs.marginSettingsUpdatedAt as number) ?? 0;

          if (serverSettings) {
            const validated = validateMarginSettings(serverSettings);
            if (validated) {
              setSettingsState(clampMarginSettings(validated));
              setUpdatedAt(serverUpdatedAt);
              debugEvent('MarginSettings.hydrate:use-server', {
                serverUpdatedAt,
                localUpdatedAt: legacyUpdatedAt,
              });
            }
          }

          setPresetsState(serverPresets);

          if (
            !isImpersonating &&
            !serverSettings &&
            (legacyUpdatedAt > 0 || legacyPresets.length > 0)
          ) {
            debugEvent('MarginSettings.hydrate:push-local', {
              localUpdatedAt: legacyUpdatedAt,
            });
            setSettingsState(clampMarginSettings(legacySettings));
            setUpdatedAt(legacyUpdatedAt);
            const migratedPresets = serverPresets.length > 0 ? serverPresets : legacyPresets.slice(0, MAX_MARGIN_PRESETS);
            setPresetsState(migratedPresets);
            await userPreferencesApi.update({
              marginSettings: clampMarginSettings(legacySettings),
              marginPresets: migratedPresets,
            });
          }

          if (cancelled || scope !== scopeRef.current) return;

          if (
            !isImpersonating &&
            (serverSettings || serverPresets.length > 0 || legacyUpdatedAt > 0 || legacyPresets.length > 0)
          ) {
            localStorage.removeItem(MARGIN_SETTINGS_STORAGE_KEY);
            localStorage.removeItem(MARGIN_PRESETS_STORAGE_KEY);
          }
        }
      } catch (err) {
        if (cancelled) return;
        setLoadFailed(true);
        loadFailedRef.current = true;
        setSyncError('설정을 불러오지 못했습니다. 다시 불러와 주세요.');
        console.error('[MarginSettingsContext] Failed to hydrate from server:', err);
        debugEvent('MarginSettings.hydrate:error', {
          error: err instanceof Error ? err.message : String(err),
        });
      } finally {
        if (!cancelled) {
          setIsHydrating(false);
          // A failed load must not let defaults overwrite the saved preferences.
          isHydratingRef.current = false;
          debugEvent('MarginSettings.hydrate:done');
        }
      }
    };

    void hydrateFromServer();
    return () => { cancelled = true; scopeRef.current = scope + 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isImpersonating, reloadVersion]);

  const setSettings = useCallback(
    (newSettings: Partial<MarginSettings>) => {
      if (isHydratingRef.current || loadFailedRef.current) return;

      setSettingsState((prev) => {
        const updated = { ...prev };

        // Validate and update numeric fields
        if (typeof newSettings.top === 'number') {
          updated.top = validateMarginValue(newSettings.top);
        }
        if (typeof newSettings.bottom === 'number') {
          updated.bottom = validateMarginValue(newSettings.bottom);
        }
        if (typeof newSettings.left === 'number') {
          updated.left = validateMarginValue(newSettings.left);
        }
        if (typeof newSettings.right === 'number') {
          updated.right = validateMarginValue(newSettings.right);
        }
        if (typeof newSettings.colSpacing === 'number') {
          updated.colSpacing = validateMarginValue(newSettings.colSpacing, true);
        }
        if (typeof newSettings.mirrorMargins === 'boolean') {
          updated.mirrorMargins = newSettings.mirrorMargins;
        }
        if (newSettings.columnCount === 1 || newSettings.columnCount === 2) {
          updated.columnCount = newSettings.columnCount;
        }
        if (typeof newSettings.columnSeparator === 'boolean') {
          updated.columnSeparator = newSettings.columnSeparator;
        }
        if ('pageDesign' in newSettings) {
          updated.pageDesign = normalizePageDesign(newSettings.pageDesign);
        }

        // Trigger server sync
        syncToServer(updated, presetsRef.current);

        return updated;
      });

      // Update local timestamp (will be replaced by server response)
      setUpdatedAt(Date.now());
    },
    [presetsRef, setSettingsState, syncToServer]
  );

  const resetSettings = useCallback(() => {
    if (isHydratingRef.current || loadFailedRef.current) return;

    setSettingsState({ ...DEFAULT_MARGIN_SETTINGS });
    setUpdatedAt(Date.now());
    syncToServer({ ...DEFAULT_MARGIN_SETTINGS }, presetsRef.current);
  }, [presetsRef, setSettingsState, syncToServer]);

  const savePreset = useCallback(
    (name: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const trimmedName = name.trim().slice(0, 100);
      if (!trimmedName) return;

      const newPreset: MarginPreset = {
        id: generatePresetId(),
        name: trimmedName,
        settings: { ...settingsRef.current },
        createdAt: Date.now(),
      };

      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = [newPreset, ...basePresets].slice(0, MAX_MARGIN_PRESETS);
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const loadPreset = useCallback(
    (id: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const preset = (user ? presetsRef.current : validatePresets(resolvePresetSource(presetsRef.current))).find((p) => p.id === id);
      if (preset) {
        const validated = validateMarginSettings(preset.settings);
        if (validated) {
          setSettingsState(clampMarginSettings(validated));
          setUpdatedAt(Date.now());
          // Sync to server to get new updatedAt
          syncToServer(clampMarginSettings(validated), presetsRef.current);
        }
      }
    },
    [presetsRef, setSettingsState, syncToServer, user]
  );

  const deletePreset = useCallback(
    (id: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = basePresets.filter((p) => p.id !== id);
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const renamePreset = useCallback(
    (id: string, newName: string) => {
      if (isHydratingRef.current || loadFailedRef.current) return;
      const trimmedName = newName.trim().slice(0, 100);
      if (!trimmedName) {
        setPresetsState((prev) => user ? prev : validatePresets(resolvePresetSource(prev)));
        return;
      }

      setPresetsState((prev) => {
        const basePresets = user ? prev : validatePresets(resolvePresetSource(prev));
        const updated = basePresets.map((p) =>
          p.id === id ? { ...p, name: trimmedName } : p
        );
        syncToServer(settingsRef.current, updated);
        return updated;
      });
    },
    [setPresetsState, settingsRef, syncToServer, user]
  );

  const retrySync = useCallback(() => {
    if (loadFailed) setReloadVersion((value) => value + 1);
    else void syncToServer(settingsRef.current, presetsRef.current);
  }, [loadFailed, presetsRef, settingsRef, syncToServer]);

  useEffect(() => {
    if (!isSyncing && (!syncError || loadFailed)) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isSyncing, syncError, loadFailed]);

  const contextValue = useMemo<MarginSettingsContextValue>(
    () => ({
      settings,
      updatedAt,
      setSettings,
      resetSettings,
      validateMarginValue,
      presets,
      savePreset,
      loadPreset,
      deletePreset,
      renamePreset,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    }),
    [
      settings,
      updatedAt,
      setSettings,
      resetSettings,
      presets,
      savePreset,
      loadPreset,
      deletePreset,
      renamePreset,
      isSyncing,
      isHydrating,
      syncError,
      loadFailed,
      retrySync,
    ]
  );

  return (
    <MarginSettingsContext.Provider value={contextValue}>
      {children}
    </MarginSettingsContext.Provider>
  );
}

export function useMarginSettings(): MarginSettingsContextValue {
  const ctx = useContext(MarginSettingsContext);
  if (!ctx) {
    throw new Error(
      'useMarginSettings must be used within a MarginSettingsProvider'
    );
  }
  return ctx;
}
